import { Injectable } from '@angular/core';

import { FixtureViewType } from '@cds/betting-offer';
import { EventModel, EventParticipant, EventParticipantType } from '@frontend/sports/betting-offer/feature/model';
import { ShortNameModule } from '@frontend/sports/betting-offer/feature/short-team-names';
import { PopularBetsConfig, Sitecore, SitecorePopularBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { PopularBetsPodTypes } from '@frontend/sports/types/components/popular-bets';

import { EventNameMapperService } from '../../event-details-common/event-name-mapper.service';

@Injectable({
    providedIn: 'root',
})
export class PopularBetsPodOptionService {
    opponentConfigWithLowerCaseKeys: {
        [key: string]: boolean;
    } = {};

    marketNameConfigWithLowerCaseKeys: {
        [key: string]: boolean;
    } = {};

    constructor(
        private sitecore: Sitecore,
        public sitecorePopularBetsConfig: SitecorePopularBetsConfig,
        private eventNameMapper: EventNameMapperService,
        private popularBetsConfig: PopularBetsConfig,
    ) {
        this.opponentConfigWithLowerCaseKeys = this.initializeConfigWithLowerCaseKeys(this.popularBetsConfig.isOpponentNameEnabled);
        this.marketNameConfigWithLowerCaseKeys = this.initializeConfigWithLowerCaseKeys(this.popularBetsConfig.isMarketNameEnabled);
    }

    private getOptionOpponentName(event: EventModel, optionName: string, opponentParticipant: EventParticipant): string {
        if (!event.isTournamentGame()) {
            return this.getOpponentName(event, optionName, opponentParticipant);
        }

        return event.name;
    }

    private getOpponentName(event: EventModel, optionName: string, opponentParticipant: EventParticipant): string {
        const isAwayTeamOption = opponentParticipant.type === EventParticipantType.Home;

        const indicator =
            event.viewType === FixtureViewType.American && isAwayTeamOption
                ? this.sitecore.globalMessages.AwayIndicator
                : this.sitecorePopularBetsConfig.messages.OpponentIndicator;
        const name = optionName;
        const opponentParticipantName = this.eventNameMapper.getParticipantName(event, opponentParticipant, ShortNameModule.PopularBets);

        return `${name} (${indicator} ${opponentParticipantName})`;
    }

    private initializeConfigWithLowerCaseKeys(item: { [key: string]: boolean }): Record<string, boolean> {
        return Object.fromEntries(Object.entries(item).map(([key, value]) => [key.toLowerCase(), value]));
    }

    getOpponentEnabledByPodType(podType: PopularBetsPodTypes): boolean {
        return this.opponentConfigWithLowerCaseKeys[podType.toLowerCase()];
    }

    getMarketNameEnabledByPodType(podType: PopularBetsPodTypes): boolean {
        return this.marketNameConfigWithLowerCaseKeys[podType.toLowerCase()];
    }

    getOpponentSelection(event: EventModel, optionName: string, eventTitle: string, opponentParticipant?: EventParticipant) {
        if (!opponentParticipant) {
            return this.optionEventTitle(optionName, eventTitle);
        }

        return this.getOptionOpponentName(event, optionName, opponentParticipant);
    }

    getSelection(
        event: EventModel,
        optionName: string,
        eventTitle: string,
        podType: PopularBetsPodTypes,
        opponentParticipant?: EventParticipant,
    ): string {
        const showMarketName = this.getMarketNameEnabledByPodType(podType);
        const showOpponent = this.getOpponentEnabledByPodType(podType);
        const optionMarketName = showMarketName ? event.optionGroups[0].name : '';
        const optionOpponent = showOpponent ? this.getOpponentSelection(event, optionName, eventTitle, opponentParticipant) : '';
        const optionEventTitle = showMarketName && !showOpponent ? this.optionEventTitle(optionName, eventTitle) : '';

        return [optionMarketName, optionOpponent || optionEventTitle || optionName].filter(Boolean).join(' - ');
    }

    private optionEventTitle(optionName: string, eventTitle: string): string {
        return `${optionName} (${eventTitle})`;
    }

    getOptionMarketName(marketName: string, optionName: string, podType: PopularBetsPodTypes): string {
        if (this.getMarketNameEnabledByPodType(podType)) {
            return [marketName, optionName].filter(Boolean).join(' - ');
        }

        return optionName;
    }
}
